import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "@/lib/apiClient";
import {
  fetchGuestTabletDeviceStatus,
  registerGuestTabletDevice,
} from "@/lib/guestTabletApi";
import type { GuestTabletDeviceStatus } from "@/types/guestTablet";

const STATUS_POLL_MS = 3_000;

export function useGuestTabletDevice(workstationCode: string) {
  const [deviceStatus, setDeviceStatus] = useState<GuestTabletDeviceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const registerInFlight = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const status = await fetchGuestTabletDeviceStatus(workstationCode);
      setDeviceStatus(status);
      setError(null);
      return status;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        const registered = await registerGuestTabletDevice(workstationCode);
        setDeviceStatus(registered);
        setError(null);
        return registered;
      }
      const message =
        err instanceof ApiError ? err.message : "Gerätestatus konnte nicht geladen werden.";
      setError(message);
      return null;
    }
  }, [workstationCode]);

  const register = useCallback(async () => {
    if (registerInFlight.current) return;
    registerInFlight.current = true;
    setLoading(true);
    setError(null);
    try {
      const status = await registerGuestTabletDevice(workstationCode);
      setDeviceStatus(status);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Gerät konnte nicht registriert werden.";
      setError(message);
    } finally {
      registerInFlight.current = false;
      setLoading(false);
    }
  }, [workstationCode]);

  useEffect(() => {
    void register();
  }, [register]);

  useEffect(() => {
    if (!deviceStatus || deviceStatus.status === "approved") {
      return;
    }
    const timer = window.setInterval(() => {
      void refresh();
    }, STATUS_POLL_MS);
    return () => window.clearInterval(timer);
  }, [deviceStatus?.status, refresh]);

  const isApproved = deviceStatus?.status === "approved";

  return {
    deviceStatus,
    loading,
    error,
    isApproved,
    refresh,
    register,
  };
}
